import { useState, useRef, useEffect } from "react";
import { useToast } from "@/components/base/Toast";
import { exportToCSV, formatExportDate } from "@/utils/exportUtils";
import { fetchApi } from "@/services/apiService";
import MiniWaveformPlayer from "./MiniWaveformPlayer";
import CallModal from "@/components/feature/CallModal";

export interface CallLogItem {
  id: string | number;
  leadName: string;
  contact_name?: string;
  phone: string;
  agent: string;
  date: string;
  dateSort?: string;
  duration: string;
  durationSeconds: number;
  duration_seconds?: number;
  status: string;
  outcome?: string;
  purpose?: string;
  hasRecording: boolean;
  recording_url?: string;
  notes?: string;
  leadId?: string | number;
  lead_id?: string | number;
}

const statusBadge: Record<string, string> = {
  Connected: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Completed: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Missed: "bg-red-50 text-red-600 border border-red-200",
  Busy: "bg-amber-50 text-amber-700 border border-amber-200",
  "No Answer": "bg-red-50 text-red-600 border border-red-200",
};

interface Props {
  calls?: CallLogItem[];
  loading?: boolean;
  onReload?: () => void;
  onViewCall: (call: any) => void;
  onOpenLogModal?: () => void;
}

export default function CallLogsTab({
  calls = [],
  loading = false,
  onReload,
  onViewCall,
  onOpenLogModal,
}: Props) {
  const { showToast } = useToast();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all");
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | number | null>(null);
  const [noteOpenFor, setNoteOpenFor] = useState<string | number | null>(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [activeDialer, setActiveDialer] = useState<CallLogItem | null>(null);
  const exportRef = useRef<HTMLDivElement>(null);

  // Close export dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    };
    if (exportOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [exportOpen]);

  const filtered = calls.filter((c) => {
    const nameMatch = (c.leadName || c.contact_name || "").toLowerCase();
    const phoneMatch = c.phone || "";
    const notesMatch = (c.notes || "").toLowerCase();
    const q = search.toLowerCase();

    if (search && !nameMatch.includes(q) && !phoneMatch.includes(q) && !notesMatch.includes(q)) {
      return false;
    }
    if (statusFilter !== "all" && c.status?.toLowerCase() !== statusFilter.toLowerCase()) {
      return false;
    }
    if (agentFilter !== "all" && c.agent !== agentFilter) {
      return false;
    }
    return true;
  });

  const handlePlay = (callId: number | string) => {
    setCurrentlyPlaying(callId);
  };

  const handlePause = () => {
    setCurrentlyPlaying(null);
  };

  const handleOpenNote = (call: CallLogItem) => {
    if (noteOpenFor === call.id) {
      setNoteOpenFor(null);
      setNoteText("");
    } else {
      setNoteOpenFor(call.id);
      setNoteText(call.notes || "");
    }
  };

  const handleSaveNote = async (callId: string | number) => {
    const trimmed = noteText.trim();
    setSavingNote(true);
    try {
      await fetchApi(`/calls/${callId}`, {
        method: "PUT",
        body: JSON.stringify({ notes: trimmed }),
      });
      showToast("Note updated successfully!", "success");
      setNoteOpenFor(null);
      if (onReload) onReload();
    } catch (err: any) {
      console.error("Save note error:", err);
      showToast(err?.message || "Failed to update note", "error");
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteCall = async (call: CallLogItem) => {
    if (!window.confirm(`Delete call record for ${call.leadName || call.phone}?`)) return;
    try {
      await fetchApi(`/calls/${call.id}`, { method: "DELETE" });
      showToast("Call record deleted", "success");
      if (onReload) onReload();
    } catch (err: any) {
      showToast(err?.message || "Failed to delete call", "error");
    }
  };

  // ── Export handlers ──
  const handleExportCallLogs = () => {
    showToast("Preparing export...", "info");
    const rows = filtered.map((c) => ({
      Lead: c.leadName || c.contact_name,
      Phone: c.phone,
      Agent: c.agent,
      "Date & Time": c.date,
      Duration: c.duration,
      Status: c.status,
      Outcome: c.outcome || "",
      Purpose: c.purpose || "",
      Notes: c.notes || "",
      "Recording Available": c.hasRecording ? "Yes" : "No",
    }));
    setTimeout(() => {
      exportToCSV(rows, `call-logs-${formatExportDate()}.csv`);
      showToast("Download started", "success");
    }, 600);
    setExportOpen(false);
  };

  const agents = Array.from(new Set(calls.map((c) => c.agent).filter(Boolean)));

  return (
    <div className="space-y-4">
      {/* Filters Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            placeholder="Search leads, phone, notes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer"
        >
          <option value="all">All Status</option>
          <option value="Connected">Connected</option>
          <option value="Missed">Missed</option>
          <option value="Busy">Busy</option>
        </select>
        <select
          value={agentFilter}
          onChange={(e) => setAgentFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer"
        >
          <option value="all">All Agents</option>
          {agents.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>

        {/* Reload button */}
        {onReload && (
          <button
            onClick={onReload}
            title="Refresh Calls List"
            className="p-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-gray-600 transition-colors cursor-pointer"
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} />
          </button>
        )}

        {/* Export Button */}
        <div className="relative ml-auto" ref={exportRef}>
          <button
            onClick={() => setExportOpen(!exportOpen)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-download-line" /> Export
          </button>
          {exportOpen && (
            <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-200 shadow-lg w-52 py-2 z-50 animate-scale-up">
              <button
                onClick={handleExportCallLogs}
                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <i className="ri-file-excel-2-line text-green-600" />
                Export Calls CSV
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Calls Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/80">
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Lead Name
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Phone
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Agent
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Date &amp; Time
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Duration
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Status / Outcome
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Recording
                </th>
                <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((call) => (
                <tr
                  key={call.id}
                  className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors group"
                >
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-900 leading-snug">
                      {call.leadName || call.contact_name || "Lead"}
                    </p>
                    {call.notes && (
                      <p className="text-[11px] text-gray-500 truncate max-w-xs mt-0.5">
                        <i className="ri-chat-1-line text-gray-400 mr-1" />
                        {call.notes}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      onClick={() => setActiveDialer(call)}
                      title="Direct In-CRM Call"
                      className="text-emerald-700 hover:text-emerald-800 font-medium hover:underline flex items-center gap-1 cursor-pointer"
                    >
                      <i className="ri-phone-fill text-emerald-600 text-xs" />
                      <span>{call.phone}</span>
                    </button>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-gray-700 font-medium">{call.agent || "Agent"}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-gray-600">{call.date}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-gray-800 font-mono font-semibold">{call.duration}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
                        statusBadge[call.status] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {call.status}
                    </span>
                    {call.outcome && call.outcome !== call.status && (
                      <span className="ml-1.5 text-[11px] text-gray-500 font-medium">
                        ({call.outcome})
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {call.hasRecording ? (
                      <MiniWaveformPlayer
                        durationSeconds={call.durationSeconds || 60}
                        callId={Number(call.id) || 1}
                        currentlyPlaying={Number(currentlyPlaying) || null}
                        onPlay={(id) => handlePlay(id)}
                        onPause={handlePause}
                      />
                    ) : (
                      <span className="text-gray-300 text-xs">&mdash;</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setActiveDialer(call)}
                        className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 transition-colors cursor-pointer"
                        title="Redial Call in CRM"
                      >
                        <i className="ri-phone-fill text-base" />
                      </button>

                      <button
                        onClick={() => handleOpenNote(call)}
                        className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                          noteOpenFor === call.id
                            ? "bg-amber-100 text-amber-700"
                            : "hover:bg-gray-100 text-gray-400 hover:text-amber-600"
                        }`}
                        title="Add/Edit Note"
                      >
                        <i className="ri-edit-line text-base" />
                      </button>

                      <button
                        onClick={() => onViewCall(call)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-indigo-600 transition-colors cursor-pointer"
                        title="View Call Details"
                      >
                        <i className="ri-eye-line text-base" />
                      </button>

                      <button
                        onClick={() => handleDeleteCall(call)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                        title="Delete Call"
                      >
                        <i className="ri-delete-bin-line text-base" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {/* Inline Note Drawer if open */}
              {noteOpenFor && (
                <tr>
                  <td colSpan={8} className="px-4 py-3 bg-amber-50/50 border-b border-amber-200">
                    <div className="flex items-start gap-3 max-w-2xl">
                      <textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Add call notes, discussion points, or next steps..."
                        className="flex-1 border border-amber-300 bg-white rounded-lg p-2.5 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-300"
                        rows={2}
                      />
                      <div className="flex items-center gap-2 pt-1">
                        <button
                          onClick={() => handleSaveNote(noteOpenFor)}
                          disabled={savingNote}
                          className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                        >
                          {savingNote ? "Saving..." : "Save Note"}
                        </button>
                        <button
                          onClick={() => {
                            setNoteOpenFor(null);
                            setNoteText("");
                          }}
                          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg transition-colors cursor-pointer"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}

              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-16 text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-phone-line text-3xl text-gray-300" />
                    </div>
                    <p className="text-lg font-semibold text-gray-700 mb-1">No call records found</p>
                    <p className="text-sm text-gray-500 mb-4">
                      {search || statusFilter !== "all"
                        ? "Try clearing filters"
                        : "Start placing or logging calls to see records here"}
                    </p>
                    {onOpenLogModal && (
                      <button
                        onClick={onOpenLogModal}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold transition-colors cursor-pointer"
                      >
                        <i className="ri-add-line" /> Log First Call
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Active In-CRM Call Modal when agent clicks dial */}
      {activeDialer && (
        <CallModal
          isOpen={true}
          onClose={() => setActiveDialer(null)}
          contactName={activeDialer.leadName || activeDialer.contact_name || "Contact"}
          phone={activeDialer.phone}
          leadId={activeDialer.leadId || activeDialer.lead_id}
          agentName={activeDialer.agent}
          onCallLogged={() => {
            if (onReload) onReload();
            setActiveDialer(null);
          }}
        />
      )}
    </div>
  );
}